import React, { useState } from "react";
import { useLang } from "@/i18n";
import { api } from "@/api";
import { toast } from "sonner";
import { OrnateHeartDivider } from "./FloralOrnaments";
import { Check, X } from "lucide-react";

const OPEN_RSVP_KEY = "wd-open-rsvp:invitation";

function readStoredOpenRsvp() {
    try {
        const raw = localStorage.getItem(OPEN_RSVP_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== "object") return null;
        if (typeof data.guest_name !== "string") return null;
        if (typeof data.attending !== "boolean") return null;
        if (typeof data.num_guests !== "number" || !Number.isFinite(data.num_guests)) return null;
        return data;
    } catch {
        return null;
    }
}

const OPEN_RSVP_CONFIRMATIONS = ["rsvpSuccess", "companionsUpdated", "rsvpDeclined", "noCompanionsThanks"];

function confirmationForStoredOpenRsvp(stored, t) {
    if (OPEN_RSVP_CONFIRMATIONS.includes(stored.confirmation)) return t(stored.confirmation);
    if (!stored.attending) return t("rsvpDeclined");
    if (stored.num_guests > 1) return t("companionsUpdated");
    return t("rsvpSuccess");
}

function writeStoredOpenRsvp(guestName, attending, numGuests, confirmation) {
    try {
        localStorage.setItem(
            OPEN_RSVP_KEY,
            JSON.stringify({
                guest_name: guestName,
                attending,
                num_guests: numGuests,
                confirmation,
            })
        );
    } catch {
        // The RSVP already saved. A full or blocked store should not fail the form.
    }
}

export default function RSVPForm({ slug, guestName, initialRsvp, requireNameForAttending = false, lockName = false }) {
    const { t } = useLang();
    const [storedOpenRsvp] = useState(() => {
        if (!requireNameForAttending || initialRsvp) return null;
        return readStoredOpenRsvp();
    });
    const [name, setName] = useState(storedOpenRsvp ? storedOpenRsvp.guest_name : (guestName || ""));
    const [attending, setAttending] = useState(
        initialRsvp ? initialRsvp.attending : storedOpenRsvp ? storedOpenRsvp.attending : null
    );
    const [numGuests, setNumGuests] = useState(() => {
        if (initialRsvp) return Math.max(0, (initialRsvp.num_guests || 1) - 1);
        if (storedOpenRsvp) return Math.max(0, storedOpenRsvp.num_guests - 1);
        return 0;
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(!!initialRsvp || !!storedOpenRsvp);
    const [confirmationMessage, setConfirmationMessage] = useState(
        storedOpenRsvp ? confirmationForStoredOpenRsvp(storedOpenRsvp, t) : ""
    );

    const rsvpGuestName = () => (lockName ? (guestName || "") : name).trim();

    const saveRsvp = async (nextAttending, options = {}) => {
        const includeCompanions = options.includeCompanions || false;
        const rsvpName = rsvpGuestName();
        if (nextAttending && requireNameForAttending && !rsvpName) {
            toast.error(t("enterYourName"));
            return;
        }
        const guestCount = nextAttending ? (includeCompanions ? 1 + numGuests : 1) : 0;
        setLoading(true);
        try {
            await api.post(`/invite/${slug}/rsvp`, {
                attending: nextAttending,
                guest_name: rsvpName,
                num_guests: guestCount,
                guest_names: [],
            });
            setAttending(nextAttending);
            setSubmitted(true);
            const confirmation = includeCompanions
                ? "companionsUpdated"
                : nextAttending ? "rsvpSuccess" : "rsvpDeclined";
            setConfirmationMessage(t(confirmation));
            if (requireNameForAttending) {
                writeStoredOpenRsvp(rsvpName, nextAttending, guestCount, confirmation);
            }
            toast.success(
                includeCompanions
                    ? t("companionsUpdated")
                    : nextAttending ? t("rsvpSuccess") : t("rsvpDeclined")
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };

    const selectAttending = (value) => {
        if (value) {
            setNumGuests((n) => Math.max(0, n || 0));
            saveRsvp(true);
        } else {
            saveRsvp(false);
        }
    };

    const updateCompanions = () => {
        if (attending !== true) return;
        saveRsvp(true, { includeCompanions: true });
    };

    const confirmNoCompanions = async () => {
        const rsvpName = rsvpGuestName();
        if (requireNameForAttending && !rsvpName) {
            toast.error(t("enterYourName"));
            return;
        }
        setNumGuests(0);
        setLoading(true);
        try {
            await api.post(`/invite/${slug}/rsvp`, {
                attending: true,
                guest_name: rsvpName,
                num_guests: 1,
                guest_names: [],
            });
            setAttending(true);
            setSubmitted(true);
            setConfirmationMessage(t("noCompanionsThanks"));
            if (requireNameForAttending) {
                writeStoredOpenRsvp(rsvpName, true, 1, "noCompanionsThanks");
            }
            toast.success(t("noCompanionsThanks"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section data-testid="rsvp-section" className="py-16 px-6">
            <div className="max-w-xl mx-auto">
                <div className="text-center">
                    <p className="text-[var(--wd-text-muted)] text-sm max-w-md mx-auto">
                        {t("honored")}
                    </p>
                    <div className="mt-6 flex justify-center"><OrnateHeartDivider /></div>
                </div>

                <div className="wd-card mt-10 p-6 md:p-8 space-y-6">
                    <div>
                        <label className="wd-label text-[var(--wd-text-muted)] block mb-2">{t("yourName")}</label>
                        {lockName ? (
                            <p
                                className="py-[0.6rem] px-1 text-base text-[var(--wd-text)]"
                                data-testid="rsvp-name-input"
                                aria-readonly="true"
                            >
                                {guestName || ""}
                            </p>
                        ) : (
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t("yourName")}
                                className="wd-input"
                                data-testid="rsvp-name-input"
                            />
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => selectAttending(true)}
                            disabled={loading}
                            data-testid="rsvp-attending-btn"
                            className={`flex items-center justify-center gap-2 py-4 rounded-xl border transition-all ${
                                attending === true
                                    ? "border-[var(--wd-primary)] bg-[var(--wd-primary)] text-white shadow-md"
                                    : "border-[var(--wd-border)] bg-white/60 text-[var(--wd-text)] hover:border-[var(--wd-primary)]"
                            }`}
                        >
                            <Check size={18} />
                            <span className="wd-heading">{t("attending")}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => selectAttending(false)}
                            disabled={loading}
                            data-testid="rsvp-not-attending-btn"
                            className={`flex items-center justify-center gap-2 py-4 rounded-xl border transition-all ${
                                attending === false
                                    ? "border-[var(--wd-rose)] bg-[var(--wd-rose)] text-white shadow-md"
                                    : "border-[var(--wd-border)] bg-white/60 text-[var(--wd-text)] hover:border-[var(--wd-rose)]"
                            }`}
                        >
                            <X size={18} />
                            <span className="wd-heading">{t("notAttending")}</span>
                        </button>
                    </div>

                    {attending === true && (
                        <div className="space-y-4 animate-in fade-in duration-500">
                            <p className="text-center wd-heading text-[var(--wd-primary)] text-lg">
                                {t("addCompanions")}
                            </p>
                            <div>
                                <label className="wd-label text-[var(--wd-text-muted)] block mb-2">
                                    {t("numberOfGuests")}
                                </label>
                                <select
                                    value={numGuests}
                                    onChange={(e) => setNumGuests(parseInt(e.target.value, 10))}
                                    className="wd-input appearance-none cursor-pointer"
                                    data-testid="rsvp-num-guests"
                                >
                                    {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                                        <option key={n} value={n}>
                                            {n} {n === 1 ? t("guest") : t("guests")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {attending === true && (
                        <div className="space-y-3">
                        <button
                            type="button"
                            onClick={updateCompanions}
                            disabled={loading}
                            className="wd-btn wd-btn-gold w-full"
                            data-testid="rsvp-submit-btn"
                        >
                            {t("submit")}
                        </button>
                        <button
                            type="button"
                            onClick={confirmNoCompanions}
                            disabled={loading}
                            className="w-full rounded-full border border-[var(--wd-accent)] px-6 py-3 text-sm font-medium text-[var(--wd-primary)] hover:bg-[var(--wd-accent-soft)] transition-colors"
                            data-testid="rsvp-no-companions-btn"
                        >
                            {t("noCompanions")}
                        </button>
                        </div>
                    )}

                    {submitted && (
                        <p className="text-center text-sm text-[var(--wd-primary)] italic" data-testid="rsvp-confirmation">
                            {confirmationMessage || (attending ? t("rsvpSuccess") : t("rsvpDeclined"))}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}
